import React, { useState, useContext } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AdminInput } from '@project/components';
import CompanySelect from './../CompanySelect/CompanySelect';
import PropertySelect from './../PropertySelect/PropertySelect';
import SpaceSelect from './../SpaceSelect/SpaceSelect';
import axios from '../../utils/axios';
import AdminLogout from '../AdminLogout/AdminLogout';
import MenuToggle from './../Shared/MenuToggle/MenuToggle';
import { useIntl } from 'react-intl';
import styles from './CreateNewProfile.module.css';
import { IoMdInformationCircle } from 'react-icons/io';
import { RootContext } from '../../hoc/RootContext/RootContext';

function CreateNewProfile() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { mobileView } = useContext(RootContext);

  const navigate = useNavigate();

  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [selectedProperty, setSelectedProperty] = useState(undefined);
  const [selectedSpace, setSelectedSpace] = useState(undefined);
  const [idpRecordId, setIdpRecordId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [idpRequired, setIDPRequired] = useState(false);

  const canCreateProfile = !!(selectedCompany && selectedProperty && selectedSpace && idpRecordId);

  const handleCreateProfile = async (e) => {
    if (!canCreateProfile) return;
    if (!idpRecordId) {
      setIDPRequired(true);
      return;
    }
    setIDPRequired(false);

    e.preventDefault();
    setErrors(null);

    try {
      setIsSaving(true);

      const result = await axios.post('/space-profiles', {
        company: selectedCompany.value,
        property: selectedProperty.value,
        display_name: selectedProperty.label,
        address1: selectedProperty.address1,
        address2: selectedSpace.value,
        city: selectedProperty.city,
        state: selectedProperty.state,
        postal: selectedProperty.postal,
        asset_guid: selectedSpace.assetGuid,
        latitude: selectedProperty.latitude,
        longitude: selectedProperty.longitude,
        basic_info: {
          integrations: {
            idp_record_id: idpRecordId
          }
        },
        idp_record_id: idpRecordId
      });

      if (result.status === 200) {
        setIsSaving(false);
        navigate(`/admin/profile/edit/${result.data.record.guid}`);
      } else {
        setErrors(`${t('error.general')}: ${result.errors}`);
      }
    } catch (e) {
      setIsSaving(false);
      if (e.response && e.response.data) {
        setErrors([`${t('error.spaceProfile.create')} (${e.response.data.errors})`]);
      } else {
        setErrors(t('error.server'));
        console.error(e);
      }
    }
  }

  return (
    <section>
      <header>
        <div className='d-flex'>
          <MenuToggle />
          <h3 className='is-page-title ml-3'>{t('CreateNewProfile.title')}</h3>
        </div>
        <div>
          <AdminLogout />
        </div>
      </header>
      <main>
        <h2>{t('CreateNewProfile.subtitle')}</h2>
        <Form className='is-common-form'>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <CompanySelect selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany} />
            </div>
            <div className='col-sm-6'>
              <PropertySelect selectedCompany={selectedCompany} setSelectedProperty={setSelectedProperty} selectedProperty={selectedProperty} />
            </div>
          </div>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <AdminInput label={t('term.address')} placeholder={t('term.address')} value={selectedProperty ? selectedProperty.address || t('CreateNewProfile.addressNotFound') : ""} readOnly={true} customstyle={styles.address} />
            </div>
            <div className='col-sm-6'>
              <SpaceSelect
                selectedCompany={selectedCompany}
                selectedProperty={selectedProperty}
                selectedSpace={selectedSpace}
                setSelectedSpace={setSelectedSpace}
              />
            </div>
          </div>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <div className="d-flex justify-content-center align-items-center position-relative w-100">
                <AdminInput
                  validationmessage={t('CreateNewProfile.idpRecord.required')}
                  label={<div>{t('term.idpRecord')} <span style={{ color: 'red' }}>*</span></div>}
                  placeholder={t('term.idpRecord')}
                  value={idpRecordId}
                  isInvalid={idpRequired}
                  onChange={(e) => { setIdpRecordId(e.target.value); setIDPRequired(false); }}
                  customstyle={`${styles.address} w-100 mr-2 data-idp-record-id`}
                />
                <div className={styles.infoIcon} onMouseOver={() => setShowInfo(true)} onMouseOut={() => setShowInfo(false)}>
                  <IoMdInformationCircle />
                  {!mobileView && showInfo &&
                    <div className={styles.infoPopup}>
                      {t('CreateNewProfile.idpRecord.details')}
                    </div>}
                </div>
                {mobileView && showInfo &&
                  <div className={styles.infoPopup}>
                    {t('CreateNewProfile.idpRecord.details')}
                  </div>}
              </div>
            </div>
          </div>
          <div>
            {errors ? <Alert key={errors} variant='danger'>{t('error.general')}: {errors}</Alert> : null}
            {selectedSpace && selectedSpace.spaceStatus && selectedSpace.spaceProfileGuid ?
              <Button className='btn-prime' onClick={() => navigate(`/admin/profile/edit/${selectedSpace.spaceProfileGuid}`)}>{t('CreateNewProfile.editProfile')}</Button> :
              <Button className='btn-prime' onClick={handleCreateProfile} disabled={!canCreateProfile || isSaving}>{t('CreateNewProfile.createProfile.button')}</Button>}
          </div>
        </Form>
      </main>
    </section>
  )
}

export default CreateNewProfile;